/**
 * MetaDefinitionsProvider
 * Contain most logic handing with Omnitech Api
 */

import React, { useContext, useMemo, useState, useEffect, useRef } from 'react'
import _ from 'lodash'
import { CacheChecksumContext, createPersistedState } from '../../cache'
import { cancelRequest } from '../../helpers'
import MetaDefinitionsContext from './meta-definitions-context'
import { fetchMetaDefinitions } from './meta-definitions-api'

// create use state hook with persisted storage
// TODO: support using different storage provider
const useMetaDefinitionsState = createPersistedState('metaDefinitions')
const useMetaDefinitionsChecksumState = createPersistedState('metaDefinitionsChecksum')

export default function MetaDefinitionsProvider({ children, config, currentLocale }) {
  // Ref
  const tempCacheRef = useRef({})

  // handle cache checksum value
  const { getChecksum } = useContext(CacheChecksumContext)
  const cacheChecksumValue = getChecksum('metaDefinitions')

  // internal state
  const [metaDefinitions, setMetaDefinitions] = useMetaDefinitionsState([])
  const [tempCache, setTempCache] = useState([])
  const [metaDefinitionsChecksum, setMetaDefinitionsChecksum] = useMetaDefinitionsChecksumState('')
  const [metaDefinitionsReady, setMetaDefinitionsReady] = useState(false)
  const [enableRefresh, setEnableRefresh] = useState(false)

  // get cache with current locale
  const availableLocales = useMemo(() => (
    _.get(config, 'locale.availableLocales')
  ), [config])
  const currentLocaleData = useMemo(() => (
    _.get(metaDefinitions, currentLocale, [])
  ), [currentLocale, metaDefinitions])
  const currentLocaleChecksum = useMemo(() => (
    _.get(metaDefinitionsChecksum, currentLocale, '')
  ), [currentLocale, metaDefinitionsChecksum])

  // use reference for temp cache
  tempCacheRef.current = tempCache

  /**
   * apiFetchCategories
   * api call to fetch meta definitions
   */
  function apiFetchMetaDefinitions(page = 1) {
    return fetchMetaDefinitions({
      includes: [
        'meta_definition_value_list_items',
      ].join(','),
      page
    })
  }

  /**
   * handleFetchNext
   * handle to call next page of metaDefinitions data
   * @param {*} nextCB
   * @param {*} endCB
   */
  async function handleFetchNext(nextCB, endCB) {
    // if next is not a function, trigger end callback directly
    if (!_.isFunction(nextCB)) {
      setMetaDefinitions({
        ..._.pick(metaDefinitions, availableLocales),
        [currentLocale]: [
          ...tempCacheRef.current
        ]
      })
      if (_.isFunction(endCB)) endCB()
      return
    }

    try {
      const { metaDefinitions: data, next } = await nextCB()
      if (_.isFunction(next)) {
        setTempCache([
          ...tempCacheRef.current,
          ...data
        ])
        handleFetchNext(next, endCB)
      } else {
        // once all pages is fetched, update the actual state used
        setMetaDefinitions({
          ..._.pick(metaDefinitions, availableLocales),
          [currentLocale]: [
            ...tempCacheRef.current,
            ...data
          ]
        })
        if (_.isFunction(endCB)) endCB()
      }
    } catch (error) {
      console.warn('fail to load metaDefinitions for cache: ', error)
    }
  }

  /**
   * handleFetchMetaDefinitions
   * get metaDefinitions for caching, will be used for making menu
   */
  async function handleFetchMetaDefinitions() {
    // use metaDefinitions in cache first
    if (
      !_.isEmpty(currentLocaleData) &&
      currentLocaleChecksum === cacheChecksumValue
    ) {
      setMetaDefinitionsReady(true)
      setEnableRefresh(true)
      return
    }

    try {
      setEnableRefresh(false)
      const { cacheChecksum, metaDefinitions: data, next } = await apiFetchMetaDefinitions()
      setTempCache(data)
      handleFetchNext(next, () => {
        setMetaDefinitionsReady(true)
        setEnableRefresh(true)
        setMetaDefinitionsChecksum({
          ..._.pick(metaDefinitionsChecksum, availableLocales),
          [currentLocale]: cacheChecksum
        })
      })
    } catch (error) {
      setEnableRefresh(true)
      console.warn('fail to load metaDefinitions for cache: ', error)
    }
  }

  /**
   * handleRefreshCategoriesCache
   * refresh departments cache when checksum is changed
   */
  async function handleRefreshCategoriesCache() {
    // when it is not required to update cache, skip the process
    if (!enableRefresh || currentLocaleChecksum === cacheChecksumValue) {
      if (_.isEmpty(currentLocaleChecksum) && !_.isEmpty(cacheChecksumValue)) {
        setMetaDefinitionsChecksum({
          ..._.pick(metaDefinitionsChecksum, availableLocales),
          [currentLocale]: cacheChecksumValue
        })
      }
      return
    }

    try {
      // start refreshing cache data
      setEnableRefresh(false)
      const { cacheChecksum, metaDefinitions: data, next } = await apiFetchMetaDefinitions()
      setTempCache(data)
      handleFetchNext(next, () => {
        setEnableRefresh(true)
        setMetaDefinitionsChecksum({
          ..._.pick(metaDefinitionsChecksum, availableLocales),
          [currentLocale]: cacheChecksum
        })
      })
    } catch (error) {
      setEnableRefresh(true)
      console.warn('fail to refresh metaDefinitions for cache', error)
    }
  }

  /**
   * get all metaDefinitions from omnitech API when app is mounted
   */
  useEffect(() => {
    handleFetchMetaDefinitions()
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchMetaDefinitions'])
    }
  }, [])

  /**
   * update metaDefinitions cache base on cache checksum
   */
  useEffect(() => {
    if (!metaDefinitionsReady || _.isUndefined(cacheChecksumValue)) return
    handleRefreshCategoriesCache()
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchMetaDefinitions'])
    }
  }, [cacheChecksumValue, metaDefinitionsReady, currentLocaleChecksum])

  const state = {
    metaDefinitions: currentLocaleData,
    metaDefinitionsReady,
    fetchMetaDefinitions,
    // TODO: will implement when need to use
    // getMetaDefinitionDynamicOptions: fetchMetaDefinitionDynamicOptions
  }

  return (
    <MetaDefinitionsContext.Provider value={state}>
      {children}
    </MetaDefinitionsContext.Provider>
  )
}
